<script setup lang="ts">
import { computed } from "vue";
import { format } from "date-fns";
import { Trash2 } from "@lucide/vue";
import { ModelType } from "@/features/BuildingProject";
import SharedModelCard from "../Shared/ModelCard.vue";

const props = defineProps<{
  model: ModelType;
}>();

// The parent owns the wrapper's models and flips isActive; mutating the prop here would hide the edit from it.
const emit = defineEmits<{
  toggle: [model: ModelType];
  remove: [model: ModelType];
}>();

// A model is a Speckle branch; commitId stays null until the connector publishes a version into it.
const isEmpty = computed(() => !props.model.commitId);

// On a connected project a row without a Speckle model is one the sync has not reached yet (RTLCA-5667).
const awaitingSpeckle = computed(() => isEmpty.value && !!props.model.streamId && !props.model.speckleModelId);

// Named on this page and not saved yet (RTLCA-5745): only a write mints a row id. Asked FIRST, because
// `awaitingSpeckle` is true of such a row too and would blame Speckle for a model it has never been
// told about. It is also the card's own half of the rail's amber mark — the only one a project with no
// 3D model connection gets, since the sync status line needs a stream — and since RTLCA-5766 the card
// wears that amber rather than only naming it: the same one flag drives the tint and the subline, so
// a chip cannot read as saved while claiming otherwise.
const isUnsaved = computed(() => !props.model.id);

const subline = computed(() => {
  // Said in the same grammar as the removal group beside it (RTLCA-5756): with one chip promising
  // "To be removed when you save", "Not saved yet" was the vaguer half of a pair.
  if (isUnsaved.value) return "Will be added when you save";
  if (awaitingSpeckle.value) return "Waiting for Speckle";
  if (isEmpty.value) return "No version published yet";
  const pulledAt = props.model.commitPulledDateTime ? new Date(props.model.commitPulledDateTime) : null;
  if (!pulledAt || Number.isNaN(pulledAt.getTime())) return "Version published";
  return `Last published ${format(pulledAt, "d MMM yyyy")}`;
});
</script>

<template>
  <SharedModelCard
    :model="props.model"
    :subline="subline"
    :empty="isEmpty"
    :pending="isUnsaved"
    @toggle="emit('toggle', props.model)">
    <!-- Only an empty model can go: a mistyped name, before anything is published to it. The parent
         still checks with Speckle that nothing was, since this list may not know about a version. -->
    <template
      v-if="isEmpty"
      #action>
      <button
        type="button"
        class="focus-ring interactive-active rounded-4 text-type-muted duration-fast ease-standard hover:bg-app-red-50 hover:text-app-red-600 flex items-center p-1 transition-colors"
        :aria-label="`Remove model ${props.model.name}`"
        @click="emit('remove', props.model)">
        <Trash2
          class="icon-xs"
          :stroke-width="1.5"
          aria-hidden="true" />
      </button>
    </template>
  </SharedModelCard>
</template>
